import React, { Fragment, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Intent } from '@blueprintjs/core';
import { LinkButton } from 'components/buttons';
import { ShowHide } from 'components/layout';
import { Breadcrumbs } from 'components/navigation';
import { NotificationInline, NotificationToaster } from 'components/notifications';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { EmployeeReviewsService } from 'services';
import { CompletedReviewDetail } from './CompletedReviewDetail';
import { SignOffResend } from './SignOffResend';

const requiredAction = "EmployeeReview:View";
const listingRoute = "/employee-reviews/completed-reviews";
const reReadFailureMessage = "Failed to refresh this review. The details shown may be out of date.";

export function EmployeeReviewView(props) {

    const { reviewId } = useParams();
    const canViewReview = UserHasAccess(requiredAction);

    const [loading, setLoading] = useState(true);
    const [invalidReview, setInvalidReview] = useState(false);
    const [review, setReview] = useState(null);

    // The completed review is an auditable point-in-time record: everything on the page renders
    // from the returned snapshot, with no live employee call. It is re-read after a sign-off
    // email is sent, because the signature URLs and the resend eligibility are both derived by
    // the API rather than held on the page.
    //
    // The read is returned so a caller that triggered it can wait for the record to land before
    // it re-enables anything acting on the old one. Rejection is handled here, so what the
    // caller waits on always settles.
    //
    // Only the first read decides whether the review can be shown at all. A re-read that fails
    // leaves the record the page already holds on screen and says it could not be refreshed,
    // because there is no longer a reason to believe the review is unreachable.
    const loadReview = useCallback((isInitialLoad = false) => {
        if (!canViewReview) {
            return Promise.resolve();
        }

        return EmployeeReviewsService.getCompletedReview(reviewId).then((reviewResponse) => {
            setReview(reviewResponse);
        }, () => {
            if (isInitialLoad) {
                setInvalidReview(true);
                return;
            }

            NotificationToaster.show(Intent.DANGER, reReadFailureMessage);
        }).finally(() => {
            setLoading(false);
        });
    }, [reviewId, canViewReview]);

    useEffect(() => {
        loadReview(true);
    }, [loadReview]);

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <ShowHide
                    evaluator={invalidReview}
                    show={(
                        <div className="row">
                            <Breadcrumbs items={props.breadcrumbs} />
                            <NotificationInline
                                allowClose={false}
                                show
                                text="This review does not exist, or you do not have permission to view it"
                                intent="danger"
                            />
                            <div>
                                <LinkButton intent="primary" text="Back to Completed Reviews" id="listing-completed-reviews" href={listingRoute} />
                            </div>
                        </div>
                    )}
                    hide={(
                        <Fragment>
                            <div className="row">
                                <Breadcrumbs items={props.breadcrumbs} />
                            </div>
                            <CompletedReviewDetail
                                review={review}
                                loading={loading}
                                signOffActions={(
                                    <SignOffResend
                                        reviewId={reviewId}
                                        signOff={review?.signOff}
                                        onSent={loadReview}
                                    />
                                )}
                            />
                        </Fragment>
                    )}
                />
            )}
            no={() => (
                <PageRestricted />
            )}
        />
    );
}

EmployeeReviewView.propTypes = {
    breadcrumbs: PropTypes.array
};
